import { LazyLoadingSectionLogger, LoggingSection } from "@multiweb/logging";
import { InvalidOperationException } from "Global/Base/Exceptions";
import { ErrorHandler } from "UI/Errors";
import { IRunMethodResult } from "../../../../../contracts";
import { IItemRightsCollection, RightType } from "../../../../../Global/Base/Rights/RightTypes";
import { IOWObject } from "../../../../../OW.Remoting/Remoting/DataTypes/IOWObject";
import { OWObject } from "../../../../../OW.Remoting/Remoting/DataTypes/OWObject";
import { OWManager } from "../../../../../OW.Remoting/Remoting/OWManager";
import { IImplicitFormInfo } from "../../../../../OW.Remoting/Remoting/OWManagerInterfaces";
import { IEnableInEditControl } from "../../../../UI.BaseControls/ControlInterfaces";
import { ActionContext, ActionType, IActionContext } from "../../../../UI.Controls/Actions/IAction";
import { MenuType } from "../../../../UI.Controls/Actions/MenuType";
import { ComponentRightsExtractor } from "../../../Rights/ComponentRightsExtractor";
import { Rights } from "../../../Rights/Rights";
import { ControlParams, IOpenAsEnabledControl, IStaticControl, IVersioned } from "../../Controls/ControlInterfaces";
import { IFRMParams } from "../../Controls/FormParameters";
import { FormManager } from "../../FormManager/IFormManager";
import { UIManager } from "../../UIManager";
import { FrameSharedMethods } from "../Frames/FrameSharedMethods";
import { MasterableFrame } from "../Frames/MasterableFrame";
import { FormLayout, FrameRefreshType } from "../IForm";
import { ModalWindowType } from "../ModalWindow";
import { ActionSharedMethods } from "./ActionSharedMethods";
import { XGAction } from "./XGAction";

const logger = LazyLoadingSectionLogger.create(LoggingSection.detailFormAction);

export class DetailFormAction extends XGAction implements IStaticControl, IEnableInEditControl, IOpenAsEnabledControl, IVersioned {
    private _showOnlyDetail: boolean;

    public formName: string;
    public masterAttr: string;
    public detailAssocName: string;
    public condition: string;
    public conditionParams: string;
    public callMethod: string;
    public openReadOnlyIf: string;
    public showOnMainForm: boolean;
    public afterGuiCreateFill: string;

    //definuje jestli se u spuštěného formuláře mají číst záznamy z verzovací tabulky nebo aktuální data
    public readVersioned: boolean;
    public refreshType: FrameRefreshType;

    private _formLayout: FormLayout;
    private readonly _rightsDefinitions: IItemRightsCollection;
    public readonly isStatic: boolean | null;

    protected _openAsEnabled: boolean = false;

    /**
    * Režim otervření formuláře (z definici formuláře)
    */
    public openMode: OpenModeType = OpenModeType.standard;

    public override get context(): ActionContext {
        if (!this.useMasterObject && (this.conditionParams || this.detailAssocName))
            return ActionContext.objectAction;
        else
            return ActionContext.frameAction;
    }

    public override get actionType(): ActionType {
        return ActionType.form;
    }

    public get openAsEnabled(): boolean {
        return this._openAsEnabled;
    }

    public set openAsEnabled(value: boolean) {
        this._openAsEnabled = value;
    }
    public get showOnlyDetail(): boolean {
        return this._showOnlyDetail;
    }

    constructor(params: ControlParams) {
        super(params);
        this.element.classList.add("DetailFormAction");
        this.setToMenu(MenuType.mtForm);
        this.formName = this.element.getAttribute("data-formname");
        this.masterAttr = this.element.getAttribute("data-masterattr");
        this.detailAssocName = this.element.getAttribute("data-detailassocname");
        this.condition = this.element.getAttribute("data-condition");
        this.conditionParams = this.element.getAttribute("data-conditionparams");
        this.callMethod = this.element.getAttribute("data-callmethod");
        this.openReadOnlyIf = this.element.getAttribute("data-openreadonlyif");
        this.showOnMainForm = this.getBoolDataAttr("showonmainform");
        this._showOnlyDetail = this.getBoolDataAttr("showonlydetail");
        this.readVersioned = this.getBoolDataAttr("readversioned");
        this.afterGuiCreateFill = this.element.getAttribute("data-AfterGUICreateFill");
        this.openMode = FrameSharedMethods.getOpenModeTypeFromAttribute(this.element, true);

        this.refreshType = FrameSharedMethods.parseFrameRefreshType(this.element.getAttribute("data-refreshactual"), this.getBoolDataAttr("refreshlist"));
        this._formLayout = FrameSharedMethods.parseFormLayout(this.element.getAttribute("data-formlayout"));

        //TODO-rites: přepsat, aby reflektovalo field/property openAsEnabled
        this._rightsDefinitions = ComponentRightsExtractor.loadRightsForTypes(this, RightType.openAsEnabled);
        this.isStatic = !this.detailAssocName && !this.conditionParams;
    }

    protected override async _internalExecute(context?: IActionContext): Promise<void> {
        const { ownerFrame, actualObject } = this.executionContext;

        const withoutInstance = (!ownerFrame && !actualObject) || context;
        if (!withoutInstance) {
            // zkontroluje se uložení změn na owneru a jeho nadřízených framech
            if (!await this._checkAndSaveForm()) {
                return;
            }
        } else {
            if (!this.ownerForm || !this.formName) {
                return;
            }
        }
        //const autoSave = false; //this.Owner.OwnerForm.AutoSave;
        let viewMode = false;

        //pokud se otevírá formulář z formuláře, který obsahuje verzovaná data tak i podřízený formulář bude číst verzovaná data, i když není verzování nastaveno na DetailFormAction
        const localReadVersioned = (this.readVersioned != null && this.readVersioned) || this.ownerForm?.readVersioned;

        UIManager.instance.showLoader();

        try {
            let formInfo: IImplicitFormInfo;
            let openFormObject: OWObject;
            const ownerForm = this.ownerForm?.formName ? this.ownerForm : this.owner?.ownerForm;
            if (!withoutInstance) {
                if (ownerFrame) {
                    await ownerFrame.waitForDataLoad();
                    await ownerFrame.waitForActualObjectSet();
                }

                const publishObjFun = async (obj: OWObject, masterAttr: string) => {
                    await OWManager.publishInstance(obj.instanceID, masterAttr, this._getRequestContext());
                    const masterAttrObj = await obj.getValue(masterAttr) as OWObject;
                    if (masterAttrObj instanceof OWObject) {
                        return masterAttrObj;
                    } else {
                        logger.warn("masterAttr does not refer to link to object");
                        return null;
                    }
                };

                try {
                    formInfo = await FrameSharedMethods.getDefaultFormName(this.formName, ownerForm, actualObject, (ownerFrame || this.owner).frameContext, this.masterAttr);
                } catch (e) {
                    ErrorHandler.instance.handleError(e);
                    return;
                }

                const masterAttr: string = formInfo.MasterAttr || this.masterAttr;
                openFormObject = !masterAttr ? actualObject : await publishObjFun(actualObject, masterAttr);

                if (openFormObject == null && this.context === ActionContext.objectAction) { // Není co zobrazit, objekt neexistuje (např. byl zrušen přes YNC dialog)
                    return;
                }
            } else {
                if (context) {
                    try {
                        if (context.ids.length === 0 || context.ids[0] === "") {
                            throw new InvalidOperationException("Cannot execute DetailFormAction.");
                        }
                        formInfo = await FrameSharedMethods.getDefaultFormName(this.formName, ownerForm, context, (ownerFrame || this.owner).frameContext, this.masterAttr);
                        openFormObject = await OWManager.getObject(context.ids[0], context.className);
                    } catch (e) {
                        ErrorHandler.instance.handleError(e);
                        return;
                    }
                } else {
                    formInfo = { FormName: this.formName };
                }
            }

            if (!withoutInstance && this.openReadOnlyIf) {
                viewMode = !(await Rights.evaluateSpecificControlRights(this._rightsDefinitions, this._rightsDefinitions.openAsEnabled, actualObject)).OpenAsEnabled;
            }

            if (!await this._methodExecute(this.callMethod, context ?? openFormObject))
                return;

            const params: IFRMParams = {
                condition: this.condition,
                conditionParamsAttrs: (this.conditionParams ? this.conditionParams.split(";") : null),
                conditionParamsObject: openFormObject,

                viewMode: viewMode,
                inLOVMode: false,
                applyLOVCondition: false,
                autoSelectFirstRow: true,
                masterObject: openFormObject,
                masterLinkToAttr: formInfo.DetailAssocName || this.detailAssocName,
                setValues: [this.element.dataset["extendedproperties"], this.afterGuiCreateFill].filter(x => x?.length).join(";"),
                hideWindowActions: true,
                selectedID: null,
                openInEdit: false,
                ownerForm: this.showOnMainForm ? null : this.ownerForm, // Předává se ownerForm, aby na něm bylo možné zakázat tlačítko Zpět a zaregistrovat nového potomka
                frameToRefreshAfterSetFormActive: (this.refreshType !== FrameRefreshType.none) ? (ownerFrame || this.owner) : null,
                frameRefreshType: this.refreshType,
                openMode: this.openMode,
                readVersioned: localReadVersioned,
                isImplicit: FrameSharedMethods.isFormNameImplicit(this.formName),
                formContext: ownerFrame?.frameContext,
                transactionContextGetter: (this._useMasterContext || this.enableInEdit) ? ownerFrame?.frameContext?.getTransactionContextId : null,
                formLayout: this._formLayout,
            };

            if (this.showOnlyDetail) {
                if (!(ownerFrame instanceof MasterableFrame)) {
                    throw new InvalidOperationException("[DetailFormAction] Cannot open detail form of non masterable frame. (set showOnlyDetail to false)");
                }
                if (formInfo.MasterAttr || this.masterAttr) {
                    throw new InvalidOperationException("[DetailFormAction] Cannot open detail form and use owner as masterFrame if masterAttr is set. (set showOnlyDetail to false)");
                }
                if (this.showOnMainForm) {
                    throw new InvalidOperationException("[DetailFormAction] Cannot open detail on main form. (set showOnlyDetail to false)");
                }
                params.detailFormMasterFrame = ownerFrame;
                params.detailFormObject = actualObject;
            }

            if (this.showOnMainForm) {
                FormManager.instance.openForm(formInfo.FormName, params);
            } else {
                await FormManager.instance.openFormModal({ formName: formInfo.FormName, parametricData: params, type: ModalWindowType.parametricForm }); //TODO: pridat do toho enumu i DetailFormAction?
            }
        } finally {
            UIManager.instance.hideLoader();
        }
    }

    private async _methodExecute(src: string, master: OWObject | IActionContext): Promise<boolean> {
        try {
            if (src) {
                const pos = src.indexOf(".");
                let result: IRunMethodResult;
                if (pos > 0) {
                    const params = new Array<string>();
                    if (master instanceof IOWObject) {
                        params.push(master.isNew ? master.instanceID : master.id);
                    }
                    if (master instanceof IActionContext && master.ids.length > 0) {
                        params.push(master.ids[0]);
                    }
                    result = await OWManager.runStaticMethodWithParams({
                        className: src.substr(0, pos),
                        methodName: src.substr(pos + 1),
                        params,
                        requestContext: this._getRequestContext(),
                    });
                } else {
                    if (master instanceof IActionContext)
                        result = await OWManager.runMethodForId({
                            id: master.ids[0],
                            className: master.className,
                            methodName: src,
                            requestContext: this._getRequestContext(),

                        });
                    else
                        result = await master.run({
                            methodName: src,
                            requestContext: this._getRequestContext(),

                        });
                }
                if (result?.messages)
                    await UIManager.instance.showResultMessages(result.messages);
            }

            return true;
        } catch (e) {
            if (master instanceof OWObject)
                ActionSharedMethods.handleErrors(e, master, this.executionContext.ownerFrame);
            else
                ErrorHandler.instance.handleError(e);

            return false;
        }
    }

    /**
     * Nastavuje přístupnost akce
     * <param name="value"></param>
     */
    public setReadOnly(_value: boolean): void {
        // Prázdná metoda
    }
}

/**
 * Rezim otervreni formulare
 * Standard - obycejne otevreni
 * Insert - otevira formular a automaticky pridava novy radek
 * Edit - otevira formular, vybira prvni radek, a nastavuje ho v rezim editace
 */
export enum OpenModeType {
    /**obycejne otevreni*/
    standard,
    /**otevira formular a automaticky pridava novy radek*/
    insert,
    /**otevira formular, vybira prvni radek, a nastavuje ho v rezim editace*/
    edit,
}
